import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import { MoreVertRounded, PersonAdd } from "@mui/icons-material";
import { useAuth } from "hooks/useAuth";
import { useOrgAdminAccess } from "hooks/useOrgAdminAccess";
import InviteTeamDialog from "components/dashboard/admin/InviteTeamDialog";
import ConfirmDialog from "components/common/ConfirmDialog";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { db } from "services/firebase";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { useNavigate } from "react-router-dom";
import { USER_ORG_ROLE, USERS_ROLE_MARKETING_EXECUTIVE } from "constants/userOrgRoles";

const CHARTMIND_STEPS = [
  { key: "record", label: "Record" },
  { key: "diagnosis", label: "Diagnosis" },
  { key: "diagnostic_plan", label: "Diag. Plan" },
  { key: "confirm_diagnosis", label: "Confirm Dx" },
  { key: "treatment", label: "Treatment" },
  { key: "discharge", label: "Discharge" },
  { key: "chart", label: "Chart" },
];

/**
 * TeamPage - Manage team members
 *
 * Allows ChartMind Managers (admins) to:
 * - Invite providers (professionals) via email
 * - View pending invitations
 * - View current team members
 */
const TeamPage = () => {
  const {
    user,
    userData,
    subscriptionData,
    organization,
    organizationMembers: members,
    organizationInvitations: invitations,
    organizationLoading: loadingData,
    refreshOrganizationTeamData,
    isBetaAdmin,
  } = useAuth();
  const { canAccess } = useOrgAdminAccess();
  const navigate = useNavigate();

  // Dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [confirmCancelInviteOpen, setConfirmCancelInviteOpen] = useState(false);
  const [confirmRevokeMarketingOpen, setConfirmRevokeMarketingOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [grantingMarketing, setGrantingMarketing] = useState(false);
  const [revokingMarketing, setRevokingMarketing] = useState(false);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [keepMenuItemOnClose, setKeepMenuItemOnClose] = useState(false);

  // Success message
  const [successMessage, setSuccessMessage] = useState("");

  // Per-user ChartMind lifetime step counts: { [uid]: { record: N, chart: N, ... } }
  const [userLifetimeSteps, setUserLifetimeSteps] = useState({});

  // Usage dialog
  const [usageMember, setUsageMember] = useState(null);

  // Track when both members and invitations have loaded at least once
  const [dataInitialized, setDataInitialized] = useState(false);
  const [initialMembersCount, setInitialMembersCount] = useState(null);
  const [initialInvitationsCount, setInitialInvitationsCount] = useState(null);

  // Wait for both members and invitations to have loaded at least once
  useEffect(() => {
    // Only proceed if organizationLoading has completed
    if (loadingData !== false) return;

    // Track the first load of each collection
    if (initialMembersCount === null) {
      setInitialMembersCount(members.length);
    }
    if (initialInvitationsCount === null) {
      setInitialInvitationsCount(invitations.length);
    }

    // Once both have been set, we know both snapshots have arrived
    if (initialMembersCount !== null && initialInvitationsCount !== null) {
      setDataInitialized(true);
    }
  }, [
    loadingData,
    members,
    invitations,
    initialMembersCount,
    initialInvitationsCount,
  ]);

  useEffect(() => {
    const orgId = organization?.id;
    if (!orgId) return undefined;

    let cancelled = false;

    db.collection("organizations")
      .doc(orgId)
      .collection("chartmind_user_stats")
      .get()
      .then((snapshot) => {
        if (cancelled) return;
        const stats = {};
        snapshot.forEach((doc) => {
          const totals = {};
          Object.values(doc.data()?.months || {}).forEach((monthData) => {
            Object.entries(monthData).forEach(([step, count]) => {
              totals[step] = (totals[step] || 0) + count;
            });
          });
          stats[doc.id] = totals;
        });
        setUserLifetimeSteps(stats);
      })
      .catch((err) => {
        if (!cancelled) console.error("[TeamPage] Failed to load user stats:", err);
      });

    return () => { cancelled = true; };
  }, [organization?.id]);

  const totalSeats = organization?.seats?.total || 0;
  const usedSeats = organization?.seats?.used || 0;
  const availableSeats = totalSeats - usedSeats;
  const hasSeatsAvailable = availableSeats > 0;

  const handleMenuOpen = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setMenuItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    // Don't clear menuItem here - let the Menu's onExited callback handle it
  };

  const handleViewUsage = () => {
    setUsageMember(menuItem);
    setMenuAnchor(null);
  };

  const handleCopyInviteLink = () => {
    if (!menuItem) return;
    const { token, displayEmail } = menuItem;
    setMenuAnchor(null);
    const url = `${window.location.origin}/join/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      setSuccessMessage(`Invite link for ${displayEmail} copied to clipboard.`);
    }).catch(() => {
      setSuccessMessage("Could not access clipboard — copy manually from the invite email.");
    });
  };

  const handleCancelInviteClick = () => {
    if (!menuItem) return;
    setKeepMenuItemOnClose(true);
    setMenuAnchor(null);
    setConfirmCancelInviteOpen(true);
  };

  const handleCancelInviteConfirm = async () => {
    if (!menuItem) return;
    setCancelling(true);
    try {
      await db.collection("invitations").doc(menuItem.id).update({
        status: "revoked",
        revokedAt: firebase.firestore.FieldValue.serverTimestamp(),
        revokedBy: user.uid,
      });
      await refreshOrganizationTeamData();
      setSuccessMessage(`Invitation to ${menuItem.displayEmail} has been cancelled.`);
      setConfirmCancelInviteOpen(false);
    } catch (err) {
      console.error("Error canceling invitation:", err);
      alert(err.message || "Failed to cancel invitation");
    } finally {
      setCancelling(false);
    }
  };

  const handleRemoveMember = () => {
    if (!menuItem) return;
    setKeepMenuItemOnClose(true); // Don't clear menuItem when menu closes
    setMenuAnchor(null); // Close menu but keep menuItem
    setConfirmRemoveOpen(true);
  };

  const isMarketingExecutiveMember = (item) =>
    item?.orgRole === USER_ORG_ROLE.MARKETING_EXECUTIVE ||
    item?.role === USERS_ROLE_MARKETING_EXECUTIVE;

  const canGrantMarketingExecutive = (item) =>
    item?.type === "member" &&
    item?.role !== "admin" &&
    item?.orgRole !== USER_ORG_ROLE.ADMIN &&
    !isMarketingExecutiveMember(item);

  const canRevokeMarketingExecutive = (item) =>
    item?.type === "member" &&
    item?.role !== USERS_ROLE_MARKETING_EXECUTIVE &&
    item?.orgRole === USER_ORG_ROLE.MARKETING_EXECUTIVE;

  const handleGrantMarketingExecutive = async () => {
    if (!menuItem || !organization?.id) return;
    setMenuAnchor(null);
    setGrantingMarketing(true);
    try {
      const appointUser = firebase.functions().httpsCallable("appointUser");
      await appointUser({
        targetUid: menuItem.id,
        targetOrgId: organization.id,
        role: USER_ORG_ROLE.MARKETING_EXECUTIVE,
      });
      await refreshOrganizationTeamData();
      const memberName =
        menuItem.displayName !== "—"
          ? menuItem.displayName
          : menuItem.displayEmail;
      setSuccessMessage(
        `${memberName} can now manage zip-slot advertising as Marketing Executive.`,
      );
    } catch (err) {
      console.error("Error granting Marketing Executive:", err);
      alert(err.message || "Failed to grant Marketing Executive role");
    } finally {
      setGrantingMarketing(false);
      setMenuItem(null);
    }
  };

  const handleRevokeMarketingClick = () => {
    if (!menuItem) return;
    setKeepMenuItemOnClose(true);
    setMenuAnchor(null);
    setConfirmRevokeMarketingOpen(true);
  };

  const handleRevokeMarketingConfirm = async () => {
    if (!menuItem || !organization?.id) return;
    setRevokingMarketing(true);
    try {
      const revokeUserRole = firebase.functions().httpsCallable("revokeUserRole");
      await revokeUserRole({
        targetUid: menuItem.id,
        targetOrgId: organization.id,
      });
      await refreshOrganizationTeamData();
      const memberName =
        menuItem.displayName !== "—"
          ? menuItem.displayName
          : menuItem.displayEmail;
      setSuccessMessage(
        `Marketing Executive access removed for ${memberName}.`,
      );
      setConfirmRevokeMarketingOpen(false);
    } catch (err) {
      console.error("Error revoking Marketing Executive:", err);
      alert(err.message || "Failed to revoke Marketing Executive role");
    } finally {
      setRevokingMarketing(false);
    }
  };

  const teamRoleLabel = (item) => {
    if (isMarketingExecutiveMember(item)) return "Marketing Executive";
    if (item.role === "admin") return "Admin";
    if (item.role === "patient") return "Patient";
    return "Member";
  };

  const confirmRemoveMember = async () => {
    if (!menuItem) return;
    setRemoving(true);

    try {
      // Call cloud function to remove member
      const removeMember = firebase.functions().httpsCallable("removeMember");
      await removeMember({ memberId: menuItem.id });
      await refreshOrganizationTeamData();

      // Show success message
      const memberName =
        menuItem.displayName !== "—"
          ? menuItem.displayName
          : menuItem.displayEmail;
      setSuccessMessage(`${memberName} has been removed from the team.`);
      setConfirmRemoveOpen(false);
    } catch (err) {
      console.error("Error removing member:", err);
      alert(err.message || "Failed to remove member");
    } finally {
      setRemoving(false);
      // Don't clear menuItem here - let the Dialog's onExited callback handle it
    }
  };

  // Combine and sort members + pending invitations
  const allTeamMembers = [
    ...members.map((m) => ({
      ...m,
      type: "member",
      sortOrder: 1,
      displayName: `${m.name || ""} ${m.lastName || ""}`.trim() || "—",
      displayEmail: m.email || m.phone || "—",
    })),
    ...invitations
      .filter((i) => i.status === "pending")
      .map((i) => ({
        ...i,
        type: "invitation",
        sortOrder: 2,
        displayName: "—",
        displayEmail: i.email,
      })),
  ].sort((a, b) => {
    // Active members first, then pending
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    // Then alphabetically by name/email
    const aName = a.displayName !== "—" ? a.displayName : a.displayEmail;
    const bName = b.displayName !== "—" ? b.displayName : b.displayEmail;
    return aName.localeCompare(bName);
  });

  if (!canAccess) {
    return (
      <Alert severity="warning">
        Team management is available to organization admins only.
      </Alert>
    );
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Team"
        subtitle="Manage your ChartMind team members."
        actions={
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite Team Member
          </Button>
        }
      />

      {/* Loading state for dynamic content (null = not initialized, true = loading) */}
      {!dataInitialized ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Team Table */}
          {allTeamMembers.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allTeamMembers.map((item) => {
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Box>
                            {item.displayName !== "—" && (
                              <Typography variant="body2" fontWeight={600}>
                                {item.displayName}
                              </Typography>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: item.displayName !== "—" ? 0.5 : 0,
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {item.displayEmail}
                              </Typography>
                              {item.type === "invitation" && (
                                <Chip label="Pending" size="small" variant="outlined" />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={item.type === "invitation" ? "text.secondary" : "text.primary"}
                          >
                            {teamRoleLabel(item)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {item.id !== user.uid && (
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, item)}>
                              <MoreVertRounded fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                No team members yet. Click "Add Member" to invite someone.
              </Typography>
            </Paper>
          )}

          {/* Actions Menu */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            TransitionProps={{
              onExited: () => {
                // Only clear menuItem if not opening remove dialog
                if (!keepMenuItemOnClose) {
                  setMenuItem(null);
                }
              },
            }}
          >
            {menuItem?.type === "invitation" ? ([
              <MenuItem key="copy" onClick={handleCopyInviteLink}>Copy Invite Link</MenuItem>,
              <MenuItem key="cancel" onClick={handleCancelInviteClick}>Cancel Invitation</MenuItem>,
            ]) : ([
              <MenuItem key="usage" onClick={handleViewUsage}>View Usage</MenuItem>,
              canGrantMarketingExecutive(menuItem) ? (
                <MenuItem
                  key="grant-marketing"
                  onClick={handleGrantMarketingExecutive}
                  disabled={grantingMarketing}
                >
                  Grant Marketing Executive
                </MenuItem>
              ) : null,
              canRevokeMarketingExecutive(menuItem) ? (
                <MenuItem
                  key="revoke-marketing"
                  onClick={handleRevokeMarketingClick}
                >
                  Revoke Marketing Executive
                </MenuItem>
              ) : null,
              <MenuItem key="remove" onClick={handleRemoveMember}>Remove from Team</MenuItem>,
            ])}
          </Menu>

          {/* Remove Member Confirmation */}
          <ConfirmDialog
            open={confirmRemoveOpen}
            onClose={() => !removing && setConfirmRemoveOpen(false)}
            title="Remove Team Member"
            message={
              <>
                Are you sure you want to remove{" "}
                {menuItem?.displayName !== "—"
                  ? menuItem?.displayName
                  : menuItem?.displayEmail}{" "}
                from the team? This will revoke their access to the
                organization.
              </>
            }
            confirmLabel="Remove"
            confirmColor="error"
            loading={removing}
            onConfirm={confirmRemoveMember}
            onExited={() => {
              setMenuItem(null);
              setKeepMenuItemOnClose(false);
            }}
          />

          <ConfirmDialog
            open={confirmRevokeMarketingOpen}
            onClose={() => !revokingMarketing && setConfirmRevokeMarketingOpen(false)}
            title="Revoke Marketing Executive"
            message={
              <>
                Remove zip-slot advertising access for{" "}
                {menuItem?.displayName !== "—"
                  ? menuItem?.displayName
                  : menuItem?.displayEmail}
                ? They will keep their existing account type.
              </>
            }
            confirmLabel="Revoke"
            confirmColor="error"
            loading={revokingMarketing}
            onConfirm={handleRevokeMarketingConfirm}
            onExited={() => {
              setMenuItem(null);
              setKeepMenuItemOnClose(false);
            }}
          />

          {/* Cancel Invitation Confirmation */}
          <ConfirmDialog
            open={confirmCancelInviteOpen}
            onClose={() => !cancelling && setConfirmCancelInviteOpen(false)}
            title="Cancel Invitation"
            message={
              <>
                Are you sure you want to cancel the invitation to{" "}
                <strong>{menuItem?.displayEmail}</strong>? They will not be able
                to join with this link.
              </>
            }
            confirmLabel="Cancel Invitation"
            loading={cancelling}
            onConfirm={handleCancelInviteConfirm}
            onExited={() => {
              setMenuItem(null);
              setKeepMenuItemOnClose(false);
            }}
          />

          {/* Usage Dialog */}
          {(() => {
            const steps = userLifetimeSteps[usageMember?.id] || {};
            const activeSteps = CHARTMIND_STEPS.filter(({ key }) => steps[key] > 0);
            const maxCount = Math.max(...activeSteps.map(({ key }) => steps[key]), 1);

            return (
              <Dialog
                open={Boolean(usageMember)}
                onClose={() => setUsageMember(null)}
                maxWidth="xs"
                fullWidth
              >
                <DialogTitle sx={{ pb: 0.5 }}>
                  {usageMember?.displayName !== "—" ? usageMember?.displayName : usageMember?.displayEmail}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    ChartMind activity
                  </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: "12px !important" }}>
                  {activeSteps.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No ChartMind activity recorded yet.
                    </Typography>
                  ) : (
                    <Box>
                      {activeSteps.map(({ key, label }) => {
                        const count = steps[key];
                        const pct = Math.round((count / maxCount) * 100);
                        return (
                          <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 88, flexShrink: 0 }}>
                              {label}
                            </Typography>
                            <Box sx={{ flex: 1, height: 8, bgcolor: "grey.100", borderRadius: 1, overflow: "hidden" }}>
                              <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: "primary.main", borderRadius: 1 }} />
                            </Box>
                            <Typography variant="body2" fontWeight={600} sx={{ width: 20, textAlign: "right", flexShrink: 0 }}>
                              {count}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </DialogContent>
              </Dialog>
            );
          })()}

          {/* Success Snackbar */}
          <Snackbar
            open={!!successMessage}
            autoHideDuration={4000}
            onClose={() => setSuccessMessage("")}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSuccessMessage("")}
              severity="success"
              sx={{ width: "100%" }}
            >
              {successMessage}
            </Alert>
          </Snackbar>

          <InviteTeamDialog
            open={inviteDialogOpen}
            onClose={() => setInviteDialogOpen(false)}
            onInvitationsSent={refreshOrganizationTeamData}
            totalSeats={totalSeats}
            usedSeats={usedSeats}
            members={members}
            invitations={invitations}
            subscriptionData={subscriptionData}
            isBetaAdmin={isBetaAdmin}
          />
        </>
      )}
    </Box>
  );
};

export default TeamPage;
